import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Lock, User, ArrowRight, Loader2 } from "lucide-react";
import {
  login as googleLogin,
  loginEmail,
  registerEmail,
  formatApiError,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const [mode, setMode] = useState(() => (new URLSearchParams(window.location.search).get("mode") === "signup" ? "signup" : "login")); // "login" | "signup"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();

  const next = new URLSearchParams(location.search).get("next") || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Email and password are required");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      if (mode === "signup") {
        await registerEmail({ email, password, name });
        toast.success("Welcome to ProSeCraft");
      } else {
        await loginEmail({ email, password });
        toast.success("Signed in");
      }
      await refresh();
      nav(next, { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
      <div
        data-testid="login-card"
        className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0F1117] p-8 shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
      >
        <div className="text-center mb-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-300 mb-2">
            ProSeCraft
          </div>
          <h1 className="font-serif text-3xl text-white font-bold">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h1>
          <p className="text-slate-400 text-sm mt-2">
            {mode === "login"
              ? "Pick up your stories where you left off."
              : "Start free — 500 gems on us."}
          </p>
        </div>

        <Button
          data-testid="login-google"
          onClick={googleLogin}
          type="button"
          className="w-full rounded-full bg-white hover:bg-slate-100 text-black font-semibold h-11"
        >
          <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0 0 12 23z" fill="#34A853" />
            <path d="M5.84 14.09c-.22-.66-.35-1.37-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.99 10.99 0 0 0 1 12c0 1.76.42 3.43 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
            <path d="M12 5.38c1.62 0 3.07.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A10.99 10.99 0 0 0 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
          </svg>
          Continue with Google
        </Button>

        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-white/10" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">or</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "signup" && (
            <div>
              <Label className="text-xs text-slate-400 font-mono uppercase tracking-widest">
                Display name (optional)
              </Label>
              <div className="mt-1 relative">
                <User className="h-4 w-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  data-testid="login-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="pl-9 bg-[#07080A] border-white/10 h-11"
                />
              </div>
            </div>
          )}
          <div>
            <Label className="text-xs text-slate-400 font-mono uppercase tracking-widest">
              Email
            </Label>
            <div className="mt-1 relative">
              <Mail className="h-4 w-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="login-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="pl-9 bg-[#07080A] border-white/10 h-11"
                required
              />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-400 font-mono uppercase tracking-widest">
              Password
            </Label>
            <div className="mt-1 relative">
              <Lock className="h-4 w-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="login-password"
                type="password"
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                className="pl-9 bg-[#07080A] border-white/10 h-11"
                required
                minLength={8}
              />
            </div>
            {mode === "login" && (
              <Link
                to="/forgot-password"
                data-testid="login-forgot-link"
                className="text-[11px] text-amber-300 hover:text-amber-200 font-mono uppercase tracking-widest mt-1 inline-block"
              >
                Forgot password?
              </Link>
            )}
          </div>

          {error && (
            <div
              data-testid="login-error"
              className="rounded-xl border border-rose-500/40 bg-rose-500/10 text-rose-200 text-sm px-4 py-3"
            >
              {error}
            </div>
          )}

          <Button
            data-testid="login-submit"
            type="submit"
            disabled={busy}
            className="w-full rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold h-11"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                {mode === "login" ? "Sign in" : "Create account"}
                <ArrowRight className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </form>

        <div className="text-center text-sm text-slate-400 mt-6">
          {mode === "login" ? (
            <>
              New here?{" "}
              <button
                data-testid="login-switch-signup"
                type="button"
                onClick={() => { setMode("signup"); setError(""); }}
                className="text-amber-300 hover:text-amber-200 font-semibold"
              >
                Create an account
              </button>
            </>
          ) : (
            <>
              Already have one?{" "}
              <button
                data-testid="login-switch-login"
                type="button"
                onClick={() => { setMode("login"); setError(""); }}
                className="text-amber-300 hover:text-amber-200 font-semibold"
              >
                Sign in
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
